import { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import FeedBackService from "../../services/FeedbackService";
import WorkTrackingAnalyticsService from "../../services/WorkTrackingAnalyticsService";

const choices = [
  { value: "yes", label: "Evet", icon: "pi pi-thumbs-up" },
  { value: "partly", label: "Kısmen", icon: "pi pi-minus-circle" },
  { value: "no", label: "Hayır", icon: "pi pi-thumbs-down" },
];

const VISIT_COUNT_KEY = "work_tracking_feedback_visit_count";
const SUBMITTED_AT_KEY = "work_tracking_feedback_submitted_at";
const MINIMUM_VISIT_COUNT = 3;
const RESURFACE_AFTER_DAYS = 90;

export default function WorkTrackingFeedback({ period, category }) {
  const [choice, setChoice] = useState("");
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [failed, setFailed] = useState(false);
  const [eligible, setEligible] = useState(false);
  const visitCounted = useRef(false);

  useEffect(() => {
    if (visitCounted.current) return;
    visitCounted.current = true;

    try {
      const submittedAt = Number(window.localStorage.getItem(SUBMITTED_AT_KEY));
      const resurfacingWindow = RESURFACE_AFTER_DAYS * 24 * 60 * 60 * 1000;
      if (Number.isFinite(submittedAt) && submittedAt > 0 && Date.now() - submittedAt < resurfacingWindow) return;

      const currentCount = Number.parseInt(window.localStorage.getItem(VISIT_COUNT_KEY) || "0", 10);
      const nextCount = (Number.isFinite(currentCount) ? currentCount : 0) + 1;
      window.localStorage.setItem(VISIT_COUNT_KEY, String(nextCount));
      setEligible(nextCount >= MINIMUM_VISIT_COUNT);
    } catch {
      setEligible(true);
    }
  }, []);

  const submit = async () => {
    if (!choice || sending) return;

    setSending(true);
    setFailed(false);
    try {
      const response = await FeedBackService.sendFeedback({
        type: "work_tracking",
        rating: choice,
        comment: comment.trim() || null,
        selectedPeriod: period || "all",
        selectedCategory: category || "all",
      });
      const result = response?.data || response;
      if (result?.success === false) throw new Error(result.message || "Geri bildirim gönderilemedi.");

      void WorkTrackingAnalyticsService.track({
        eventType: "feedback_submit",
        workItemType: choice.toUpperCase(),
        selectedPeriod: period,
        selectedCategory: category,
      });
      try {
        window.localStorage.setItem(SUBMITTED_AT_KEY, String(Date.now()));
        window.localStorage.removeItem(VISIT_COUNT_KEY);
      } catch {
        // Depolama kapalıysa geri bildirim yine de başarıyla tamamlanmış sayılır.
      }
      setSubmitted(true);
    } catch (error) {
      console.error("Work tracking feedback error:", error);
      setFailed(true);
    } finally {
      setSending(false);
    }
  };

  if (!eligible) return null;

  if (submitted) {
    return (
      <section className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300">
        <i className="pi pi-check-circle text-xl" />
        <div>
          <strong className="block text-sm">Teşekkür ederiz.</strong>
          <span className="text-xs font-medium">Geri bildiriminiz İş Takibi’ni geliştirmemize yardımcı olacak.</span>
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950 sm:p-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-xl">
          <h2 className="m-0 text-sm font-black text-slate-900 dark:text-white">İş Takibi günlük işlerinizi kolaylaştırdı mı?</h2>
          <p className="mb-0 mt-1 text-xs font-medium text-slate-500 dark:text-slate-400">Deneyiminizi paylaşın; sonraki geliştirmeleri gerçek kullanımınıza göre şekillendirelim.</p>
          <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="İş Takibi değerlendirmesi">
            {choices.map((item) => (
              <button
                key={item.value}
                type="button"
                aria-pressed={choice === item.value}
                onClick={() => setChoice(item.value)}
                className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-black transition-colors ${choice === item.value ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:text-blue-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"}`}
              >
                <i className={item.icon} /> {item.label}
              </button>
            ))}
          </div>
        </div>

        <div className="w-full lg:max-w-xl">
          <InputTextarea
            value={comment}
            onChange={(event) => setComment(event.target.value.slice(0, 500))}
            rows={2}
            autoResize
            placeholder="Neyi değiştirmemizi veya eklememizi istersiniz? (İsteğe bağlı)"
            aria-label="İş Takibi hakkında isteğe bağlı geri bildirim notu"
            className="w-full !rounded-xl !border-slate-200 text-sm dark:!border-slate-700 dark:!bg-slate-900 dark:!text-white"
          />
          <div className="mt-2 flex items-center justify-between gap-3">
            <span className={`text-[10px] font-bold ${failed ? "text-rose-600" : "text-slate-400"}`}>
              {failed ? "Gönderilemedi. Lütfen tekrar deneyin." : `${comment.length}/500`}
            </span>
            <Button
              label="Geri Bildirim Gönder"
              icon="pi pi-send"
              disabled={!choice}
              loading={sending}
              onClick={submit}
              className="p-button-sm !rounded-xl !bg-blue-600 !text-xs !font-black"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
